import { Formik, Field, Form } from "formik";
import {
  InputGroup,
  FieldWrappers,
  Label,
  SubmitBtn,
  SideCancelBtn,
  Wrapper,
  ScrollableContent,
  ActionSection,
  ModalHeading,
  ModalSubHeading,
  SelectionCard,
  CardWrapper,
} from "../Styles/InputStyles";
import * as Yup from "yup";
import { useState } from "react";
import { MdEdit } from "react-icons/md";
import CategoryCard from "../CategoryCard";
import SelectItemModal from "../SelectItemModal";
import SelectOptionModal from "../SelectOptionModal";
import Config, {
  inventoryAge,
  inventoryBelongsTo,
  listing_type,
} from "../../Config";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const BasicDetails = ({
  onClose,
  step,
  setStep,
  setStepOneData,
  stepOneData,
}) => {
  const owners = useSelector((state) => state.OwnerReducer.owners);
  const brokers = useSelector((state) => state.BrokerReducer.brokers);
  const navigate = useNavigate();
  const inventoryAgeOptions = inventoryAge.map((role) => ({
    label: role.label,
    value: role.value,
  }));

  const listingTypeOptions = listing_type.map((role) => ({
    label: role.label,
    value: role.value,
    disabled: role.disabled || false,
  }));

  const [selectedOwner, setSelectedOwner] = useState(null);
  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isListingModalOpen, setIsListingModalOpen] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [isBrokerModalOpen, setIsBrokerModalOpen] = useState(false);

  const initialValues = stepOneData  || {
    inventoryAge: "",
    belongsToType: inventoryBelongsTo[0]?.value || 1,

    ownerName: "",
    inventoryName: "",
    ownerId: "",

    brokerName: "",
    brokerId: "",

    mobile: "",
    listingType: "",
  };

  const validationSchema = Yup.object({
    ownerName: Yup.string().when("belongsToType", {
      is: Config.USER_TYPE_RETAIL.LANDLORD,
      then: (schema) =>
        schema
          .required("Owner name is required")
          .matches(/^[A-Za-z]+(?: [A-Za-z]+)*$/, "Only alphabets allowed")
          .min(4, "Minimum 4 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),

    brokerName: Yup.string().when("belongsToType", {
      is: Config.USER_TYPE_RETAIL.CHANNEL_PARTNER,
      then: (schema) =>
        schema
          .required("Broker name is required")
          .matches(/^[A-Za-z]+(?: [A-Za-z]+)*$/, "Only alphabets allowed")
          .min(4, "Minimum 4 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),

    mobile: Yup.string()
      .required("Mobile number is required")
      .matches(/^\d{10}$/, "Mobile number should be exactly 10 digits"),

    inventoryName: Yup.string().required("Enter the listing name"),
    listingType: Yup.string().required("Select the listing type"),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    const formData =
      values.belongsToType === Config.USER_TYPE_RETAIL.CHANNEL_PARTNER
        ? {
            belongsToType: values.belongsToType,
            brokerName: values.brokerName,
            belongsToId: values.brokerId,
            mobile: values.mobile,
            listingType: values.listingType,
            inventoryName: values.inventoryName,
          }
        : {
            belongsToType: values.belongsToType,
            ownerName: values.ownerName,
            belongsToId: values.ownerId,
            mobile: values.mobile,
            listingType: values.listingType,
            inventoryName: values.inventoryName,
          };

    setStepOneData(formData);
    setStep(step + 1);
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      // enableReinitialize={true}
      validationSchema={validationSchema}
      validateOnBlur={true}
      validateOnChange={true}
    >
      {({
        values,
        setFieldValue,
        setFieldTouched,
        errors,
        touched,
        isSubmitting,
      }) => {
        const handleRoleSelect = (selectedValue) => {
          setFieldValue("inventoryAge", selectedValue);
        };

        const getCurrentListingLabel = () => {
          const selectedListing = listing_type.find(
            (role) => role.value === values?.listingType,
          );
          return selectedListing
            ? selectedListing.label
            : "Select listing type";
        };

        const handleListingSelect = (selectedValue) => {
          setFieldValue("listingType", selectedValue);
        };

        return (
          <Form>
            <ModalSubHeading>Step 1</ModalSubHeading>
            <ModalHeading>Owner & Listing Info</ModalHeading>
            <ScrollableContent>
              <Wrapper>
                <InputGroup>
                  <Label>Inventory Belongs To *</Label>
                  <CardWrapper>
                    {inventoryBelongsTo.map((mode) => (
                      <SelectionCard
                        key={mode.value}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setFieldValue("belongsToType", mode.value);

                          // Clear previous selections
                          setFieldValue("ownerName", "");
                          setFieldValue("ownerId", "");
                          setFieldValue("brokerName", "");
                          setFieldValue("brokerId", "");
                          setFieldValue("mobile", "");

                          setSelectedOwner(null);
                          setSelectedBroker(null);
                        }}
                        className={
                          values.belongsToType === mode.value
                            ? "bg-[#26A69A] text-white"
                            : "bg-gray-100 text-gray-600"
                        }
                      >
                        <span className="text-sm">{mode.label}</span>
                      </SelectionCard>
                    ))}
                  </CardWrapper>
                </InputGroup>
                {/* Landlord Name and Mobile */}
                <InputGroup>
                  <Label htmlFor="ownerName">
                    {values.belongsToType ===
                    Config.USER_TYPE_RETAIL.CHANNEL_PARTNER
                      ? "Broker's Name *"
                      : "Owner's Name *"}
                  </Label>

                  <CategoryCard
                    title={
                      values.belongsToType ===
                      Config.USER_TYPE_RETAIL.CHANNEL_PARTNER
                        ? values.brokerName || "Select Broker"
                        : values.ownerName || "Select Owner"
                    }
                    selectedCategory={
                      values.belongsToType ===
                      Config.USER_TYPE_RETAIL.CHANNEL_PARTNER
                        ? selectedBroker
                        : selectedOwner
                    }
                    onClick={() => {
                      if (
                        values.belongsToType ===
                        Config.USER_TYPE_RETAIL.CHANNEL_PARTNER
                      ) {
                        setIsBrokerModalOpen(true);
                      } else {
                        setIsOwnerModalOpen(true);
                      }
                    }}
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      Can't find the{" "}
                      {values.belongsToType ===
                      Config.USER_TYPE_RETAIL.CHANNEL_PARTNER
                        ? "broker"
                        : "owner"}
                      ?
                    </p>

                    <button
                      type="button"
                      onClick={() => {
                        if (
                          values.belongsToType ===
                          Config.USER_TYPE_RETAIL.CHANNEL_PARTNER
                        ) {
                          navigate("/brokers");
                        } else {
                          navigate("/owners");
                        }
                      }}
                      className="text-sm font-semibold text-primary-color hover:underline"
                    >
                      + Add New
                    </button>
                  </div>
                  {values.belongsToType ===
                    Config.USER_TYPE_RETAIL.CHANNEL_PARTNER &&
                    touched.brokerName &&
                    errors.brokerName && (
                      <p className="text-rose-600 text-sm mt-1">
                        {errors.brokerName}
                      </p>
                    )}
                  {values.belongsToType === Config.USER_TYPE_RETAIL.LANDLORD &&
                    errors.ownerName &&
                    touched.ownerName && (
                      <p className="text-rose-600 text-sm mt-1">
                        {errors.ownerName}
                      </p>
                    )}
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="inventoryName">Listing Name *</Label>
                  <FieldWrappers>
                    <Field
                      type="text"
                      name="inventoryName"
                      id="inventoryName"
                      autoComplete="off"
                      // required
                      placeholder="Listing name"
                    />
                  </FieldWrappers>
                  {touched.inventoryName && errors.inventoryName && (
                    <p className="text-rose-600 text-sm mt-1">
                      {errors.inventoryName}
                    </p>
                  )}
                </InputGroup>
                {/* Listing Type */}
                <InputGroup>
                  <Label>Listing Type *</Label>
                  <FieldWrappers onClick={() => setIsListingModalOpen(true)}>
                    <Field
                      type="text"
                      name="listingType"
                      id="listingType"
                      autoComplete="off"
                      readOnly
                      value={getCurrentListingLabel()}
                      className="w-full border-none outline-none bg-transparent cursor-pointer"
                      placeholder="Select listing type"
                    />
                    <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                  </FieldWrappers>
                  {touched.listingType && errors.listingType && (
                    <p className="text-rose-600 text-sm mt-1">
                      {errors.listingType}
                    </p>
                  )}
                </InputGroup>
              </Wrapper>
            </ScrollableContent>

            <ActionSection>
              <SideCancelBtn type="button" onClick={onClose}>
                Cancel
              </SideCancelBtn>
              <SubmitBtn type="submit" disabled={isSubmitting}>
                Next
              </SubmitBtn>
            </ActionSection>

            <SelectItemModal
              isOpen={isOwnerModalOpen}
              onClose={() => setIsOwnerModalOpen(false)}
              onSelect={(owner) => {
                setSelectedOwner(owner);
                setFieldValue("ownerName", owner.name);
                setFieldValue("ownerId", owner.id);
                setFieldValue("mobile", owner.mobile);
              }}
              options={owners}
              selectedItem={selectedOwner}
              title="Select Owner"
              searchPlaceholder="Search owner..."
              idKey="id"
              nameKey="name"
              descriptionKeys={["mobile"]}
            />

            <SelectOptionModal
              isOpen={isRoleModalOpen}
              onClose={() => setIsRoleModalOpen(false)}
              title="Select Owner Role"
              options={inventoryAgeOptions}
              selectedValue={values.inventoryAge}
              onSelect={handleRoleSelect}
              searchPlaceholder="Search..."
            />
            <SelectOptionModal
              isOpen={isListingModalOpen}
              onClose={() => setIsListingModalOpen(false)}
              title="Select Listing Type"
              options={listingTypeOptions}
              selectedValue={values.listingType}
              onSelect={handleListingSelect}
              searchPlaceholder="Search listing type..."
            />

            <SelectItemModal
              isOpen={isBrokerModalOpen}
              onClose={() => setIsBrokerModalOpen(false)}
              onSelect={(broker) => {
                setSelectedBroker(broker);
                setFieldValue("brokerName", broker.name);
                setFieldValue("brokerId", broker.id);
                setFieldValue("mobile", broker.mobile);
              }}
              options={brokers}
              selectedItem={selectedBroker}
              title="Select Broker"
              searchPlaceholder="Search broker..."
              idKey="id"
              nameKey="name"
              descriptionKeys={["mobile"]}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default BasicDetails;
